'use client';

import React from 'react';

interface AvatarCircleProps {
  fullName: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function AvatarCircle({ fullName, size = 'md', className = '' }: AvatarCircleProps) {
  // Extract initials from full name (e.g., "Shant Banosian" → "SB")
  const getInitials = (name: string): string => {
    if (!name || typeof name !== 'string') return '??';

    const words = name.trim().split(/\s+/);
    if (words.length === 0) return '??';

    if (words.length === 1) {
      // Single word: take first 2 characters
      return words[0].substring(0, 2).toUpperCase();
    }

    // Multiple words: take first letter of first and last word
    const firstInitial = words[0][0];
    const lastInitial = words[words.length - 1][0];
    return (firstInitial + lastInitial).toUpperCase();
  };

  const initials = getInitials(fullName);

  // Size variants
  const sizeClasses = {
    sm: 'w-10 h-10 text-sm',
    md: 'w-16 h-16 text-xl',
    lg: 'w-24 h-24 text-3xl'
  };

  return (
    <div
      className={`
        ${sizeClasses[size]}
        rounded-full
        bg-gradient-to-br from-blue-500 to-blue-700
        text-white
        font-bold
        flex items-center justify-center
        shadow-lg
        border-4 border-white
        ${className}
      `}
      title={fullName}
      aria-label={`Avatar for ${fullName}`}
    >
      {initials}
    </div>
  );
}
